import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { ProfitRole, ProfitSettings, ProfitSettingsUpdate } from '@/types/profitSettings'
import type { PositionProfitRoleResponse } from '@/lib/profitSettingsApi'

export const profitSettingsQueryKey = ['profit-settings'] as const

export function useProfitSettings() {
  return useQuery<ProfitSettings>({
    queryKey: profitSettingsQueryKey,
    queryFn: async () => {
      const res = await api.get<ProfitSettings>('/profit-settings')
      return res.data
    },
  })
}

export function useUpdateProfitSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ProfitSettingsUpdate) => {
      const res = await api.patch<ProfitSettings>('/profit-settings', payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: profitSettingsQueryKey })
      qc.invalidateQueries({ queryKey: ['profitTaking'] })
    },
  })
}

interface UpdateRoleVars {
  positionId: number
  role: ProfitRole
}

export function useUpdatePositionRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ positionId, role }: UpdateRoleVars) => {
      const res = await api.patch<PositionProfitRoleResponse>(
        `/positions/${positionId}/profit-role`,
        { role },
      )
      return res.data
    },
    onMutate: async ({ positionId, role }) => {
      await qc.cancelQueries({ queryKey: ['positions'] })
      const previous = qc.getQueryData<PositionMini[]>(['positions'])
      if (previous) {
        qc.setQueryData<PositionMini[]>(
          ['positions'],
          previous.map((p) => (p.id === positionId ? { ...p, profit_role: role } : p)),
        )
      }
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(['positions'], context.previous)
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['positions'] })
      qc.invalidateQueries({ queryKey: ['profitTaking'] })
    },
  })
}

export interface PositionMini {
  id: number
  name: string
  isin: string
  currency: string
  profit_role: ProfitRole
}

export function usePositionsForSettings() {
  return useQuery<PositionMini[]>({
    queryKey: ['positions'],
    queryFn: async () => {
      const res = await api.get<PositionMini[]>('/positions/')
      return res.data
    },
  })
}
